import { useEffect, useState } from "react";

// Builds an empty billing address, seeding only the country (which is derived
// from the case, not user-editable in the form).
const emptyBillingAddress = (country) => ({
  email: "",
  name: "",
  vat_id: "",
  address_line_1: "",
  address_line_2: "",
  postal_code: "",
  city: "",
  country: country || "",
  custom_fields: [],
});

const copyCustomFields = (fields) =>
  Array.isArray(fields) ? fields.map((field) => ({ ...field })) : [];

const mapSourceToBillingAddress = (source, country, name) => ({
  email: source.email || "",
  name,
  vat_id: source.vat_id || "",
  address_line_1: source.address_line_1 || "",
  address_line_2: source.address_line_2 || "",
  postal_code: source.postal_code || "",
  city: source.city || "",
  country,
  custom_fields: copyCustomFields(source.custom_fields),
});

/**
 * Owns billing-address form state and seeds it from source data.
 *
 * Pass these values (not parent `props`/`caseInfo`) so a parent re-render
 * after generateInvoice does not reset the form:
 *   caseId               – safety net when switching cases
 *   billingAddressSource – case.billing_address, if present
 *   contactDetails       – fallback when billing_address is missing
 *   country              – case country from Redux
 *
 * Returns `{ billingAddress, updateBillingField, updateCustomFieldValue }`.
 */
export function useBillingAddressSeed({
  caseId,
  billingAddressSource,
  contactDetails,
  country,
}) {
  const [billingAddress, setBillingAddress] = useState(() =>
    emptyBillingAddress(country),
  );

  const updateBillingField = (key, value) => {
    setBillingAddress((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const updateCustomFieldValue = (index, value) => {
    setBillingAddress((prev) => {
      const customFields = [...(prev.custom_fields || [])];
      customFields[index] = {
        ...customFields[index],
        value,
      };

      return { ...prev, custom_fields: customFields };
    });
  };

  // Re-seed only when the source data changes. Depending on parent
  // `props`/`caseInfo` identity would re-run after generateInvoice and clobber
  // in-progress edits.
  useEffect(() => {
    if (billingAddressSource) {
      setBillingAddress(
        mapSourceToBillingAddress(
          billingAddressSource,
          country,
          billingAddressSource.name || "",
        ),
      );
    } else if (contactDetails) {
      setBillingAddress(
        mapSourceToBillingAddress(
          contactDetails,
          country,
          contactDetails.company
            ? contactDetails.company
            : contactDetails.name || "",
        ),
      );
    } else {
      setBillingAddress(emptyBillingAddress(country));
    }
  }, [caseId, billingAddressSource, contactDetails, country]);

  return { billingAddress, updateBillingField, updateCustomFieldValue };
}
